import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionValidated, setSessionValidated] = useState(false);

  // Logout function - defined early so it can be used in interceptor
  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('user');
    // Clear the change-password prefill hint (see login/change-password).
    sessionStorage.removeItem('pw_hint');
    setSessionValidated(false);
  }, []);

  // Validate session with backend
  const validateSession = useCallback(async (sessionToken) => {
    if (!sessionToken) return false;
    
    try {
      const response = await axios.get(`${API}/auth/validate?token=${sessionToken}`);
      if (response.data.valid && response.data.user) {
        setUser(response.data.user);
        sessionStorage.setItem('user', JSON.stringify(response.data.user));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Session validation failed:', error);
      return false;
    }
  }, []);

  // Setup axios interceptor for 401 handling
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Only logout if we're on a protected route (not login/register)
          const path = window.location.pathname;
          if (path !== '/login' && path !== '/auth/callback' && !path.startsWith('/shared/')) {
            console.log('Session expired - logging out');
            logout();
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  // Initialize auth state from sessionStorage (per-tab) so two browser tabs
  // can hold two different sessions simultaneously — e.g. one tab signed in
  // as CEO, another as HOD — without clobbering each other across refreshes.
  // localStorage is scoped to the origin and shared across tabs, which made
  // the last-written token win for every tab.
  useEffect(() => {
    // One-time migration: if a leftover token exists in localStorage from
    // before this fix, copy it into THIS tab's sessionStorage and clear the
    // localStorage copy so cross-tab clobbering can't happen again.
    try {
      const legacyToken = localStorage.getItem('auth_token');
      const legacyUser = localStorage.getItem('user');
      if (legacyToken && !sessionStorage.getItem('auth_token')) {
        sessionStorage.setItem('auth_token', legacyToken);
        if (legacyUser) sessionStorage.setItem('user', legacyUser);
      }
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    } catch {
      /* storage unavailable — fall through */
    }

    const initAuth = async () => {
      const storedToken = sessionStorage.getItem('auth_token');
      const storedUser = sessionStorage.getItem('user');
      
      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        
        // Validate session in background (don't block UI)
        const isValid = await validateSession(storedToken);
        if (!isValid) {
          console.log('Stored session invalid - clearing');
          logout();
        }
        setSessionValidated(true);
      }
      setLoading(false);
    };
    
    initAuth();
  }, [validateSession, logout]);

  // Periodic session validation (every 5 minutes)
  useEffect(() => {
    if (!token || !sessionValidated) return;
    
    const interval = setInterval(async () => {
      const isValid = await validateSession(token);
      if (!isValid) {
        console.log('Session expired during periodic check');
        logout();
        window.location.href = '/login';
      }
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(interval);
  }, [token, sessionValidated, validateSession, logout]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      // Handle both old format (direct) and new standardized format (nested in data)
      const responseData = response.data.data || response.data;
      const { access_token, user: userData } = responseData;

      setToken(access_token);
      setUser(userData);
      setSessionValidated(true);
      sessionStorage.setItem('auth_token', access_token);
      sessionStorage.setItem('user', JSON.stringify(userData));
      // UX-only prefill hint: cache the plaintext password in sessionStorage
      // so the Change Password page can pre-fill the Current Password field
      // without depending on Chrome's password manager. Bounded blast radius:
      //   - sessionStorage (per-tab, cleared on tab close, cleared on logout)
      //   - NEVER localStorage — that would persist across restarts.
      // Security note: any script running in-page (including a successful
      // XSS payload) can read this. That's the accepted tradeoff for the UX;
      // do not extend the pattern to more sensitive data.
      sessionStorage.setItem('pw_hint', password);

      return { success: true };
    } catch (error) {
      // Handle standardized error format
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Login failed';
      return { success: false, error: errorMsg };
    }
  };

  const register = async (email, password, full_name, company) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        full_name,
        company,
      });
      // Handle both old format (direct) and new standardized format (nested in data)
      const responseData = response.data.data || response.data;
      const { access_token, user: userData } = responseData;
      
      setToken(access_token);
      setUser(userData);
      setSessionValidated(true);
      sessionStorage.setItem('auth_token', access_token);
      sessionStorage.setItem('user', JSON.stringify(userData));
      
      return { success: true };
    } catch (error) {
      // Handle standardized error format
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Registration failed';
      return { success: false, error: errorMsg };
    }
  };

  const value = {
    user,
    setUser,
    token,
    setToken,
    login,
    register,
    logout,
    loading,
    sessionValidated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};